import { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import PageWrapper from 'components/Common/PageWraper';
import NumberWidget from 'components/Widgets/NumberWidget';
import FilterDrawer from 'components/FilterDrawer/FilterDrawer';
import { FilterState, GenericOption } from 'components/FilterDrawer/types';
import ArrowIconRed from '../../assets/growth arrow up_red.svg';
import ArrowIconGreen from '../../assets/growth arrow down_green.svg';
import {
	fetchFinOpsMonthlySummary,
	fetchFinOpsPrograms,
	fetchFinOpsSectors,
	fetchFinOpsPlatforms,
	fetchFinOpsSpendingByType,
	fetchFinOpsChartSeries
} from 'service/service';
import { formatNumberByUnits, calculateVariancePercentage } from 'utils/finopsDataAdapter';
import FinOpsMonthlyTrendChart from './FinOpsMonthlyTrendChart';
import useFinOpsSideDrawer from './useFinOpsDrawer';
import SpendTable from './SpendTable';
import TrendIncrementWidget from './TrendIncrementWidget';
import useFinOpsSectorFilter from './useFinOpsSectorFilter';
import { useFinOpsTab } from 'Context/contexts/FinOpsTabContext';

interface SpendData {
	name: string;
	totalSpent: number;
	totalForecast: number;
	aop: number;
	grandTotal?: number;
}

interface HierarchicalData {
	name: string;
	totalSpent: number;
	percentage: number;
	children?: HierarchicalData[];
}

const FinOpsExecutiveDashboard = () => {
	const [summary, setSummary] = useState<any>(null);
	const [loading, setLoading] = useState(false);
	const { handleOpenInsights } = useFinOpsSideDrawer();
	const { filterMonth, filterCustomStartDate, filterCustomEndDate, setHasFilters, setOnFiltersOpen } = useFinOpsTab();

	const [programName, setProgramName] = useState('all');
	const [sectorName, setSectorName] = useState('all');
	const [platformName, setPlatformName] = useState('all');
	const { shouldFilter, sectorNames } = useFinOpsSectorFilter();
	const effectiveSectorName = shouldFilter && sectorNames.length > 0 ? sectorNames[0] : sectorName;
	const [currentFilterYear, setCurrentFilterYear] = useState(new Date().getFullYear());

	const [programsList, setProgramsList] = useState<string[]>([]);
	const [sectorsList, setSectorsList] = useState<{ name: string; isAllowed: boolean }[]>([]);
	const [platformsList, setPlatformsList] = useState<string[]>([]);

	const [spendByProgram, setSpendByProgram] = useState<SpendData[]>([]);
	const [spendBySector, setSpendBySector] = useState<SpendData[]>([]);
	const [spendByPlatform, setSpendByPlatform] = useState<SpendData[]>([]);
	const [globalSectorTotal, setGlobalSectorTotal] = useState(0);
	const [chartSeries, setChartSeries] = useState<any[]>([]);
	const [trendData, setTrendData] = useState<any>(null);
	const [trendLoading, setTrendLoading] = useState(false);
	const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

	const activeDrawerField = (() => {
		if (programName !== 'all') return 'program';
		if (sectorName !== 'all') return 'sector';
		if (platformName !== 'all') return 'platform';
		return null;
	})();
	const [pendingDrawerField, setPendingDrawerField] = useState<string | null>(null);
	const effectiveDrawerField = settingsDrawerOpen ? (pendingDrawerField ?? activeDrawerField) : null;

	const hasActiveFilters = programName !== 'all' || sectorName !== 'all' || platformName !== 'all';

	useEffect(() => { setHasFilters(hasActiveFilters); }, [hasActiveFilters]);
	useEffect(() => {
		setOnFiltersOpen(() => setSettingsDrawerOpen(true));
		return () => { setHasFilters(false); setOnFiltersOpen(() => { }); };
	}, []);

	const getActiveChartData = () => {
		return chartSeries.map((item: any) => ({
			month: item.month,
			name: item.programName || item.sectorName || item.platformName || 'Unknown',
			actualCost: item.actualCost || 0,
			forecastCost: item.forecastCost || null
		}));
	};

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const [programsResponse, sectorsResponse, platformsResponse] = await Promise.all([
					fetchFinOpsPrograms(),
					fetchFinOpsSectors(),
					fetchFinOpsPlatforms()
				]);

				setProgramsList(programsResponse.data?.programs?.map((p: any) => p.name) || []);
				setSectorsList(sectorsResponse.data?.sectors || []);
				setPlatformsList(platformsResponse.data?.platforms?.map((p: any) => p.name) || []);
			} catch (error) {
				console.error('Error fetching FinOps filter data:', error);
			}
		};
		fetchInitialData();
	}, []);

	useEffect(() => {
		const fetchFilteredData = async () => {
			setLoading(true);
			setTrendLoading(true);
			try {
				const dateParams = parseDateFilter(filterMonth);

				const [summaryResponse, programResponse, sectorResponse, platformResponse, chartSeriesResponse] =
					await Promise.all([
						fetchFinOpsMonthlySummary(dateParams.year, {
							programName,
							sectorName: effectiveSectorName,
							platformName,
							startMonth: dateParams.startMonth,
							endMonth: dateParams.endMonth
						}),
						fetchFinOpsSpendingByType('Program', dateParams.year, 5),
						fetchFinOpsSpendingByType('Sector', dateParams.year, 5),
						fetchFinOpsSpendingByType('Platform', dateParams.year, 5),
						fetchFinOpsChartSeries(dateParams.year, {
							programName,
							sectorName: effectiveSectorName,
							platformName,
							startMonth: dateParams.startMonth,
							endMonth: dateParams.endMonth
						})
					]);

				if (summaryResponse.data) {
					setSummary(summaryResponse.data);
					setTrendData({
						increment2026: summaryResponse.data.increment2026,
						sparklineData: summaryResponse.data.sparklineData
					});
				}
				const sectors: SpendData[] = sectorResponse.data || [];

				setSpendByProgram(programResponse.data || []);
				setSpendBySector(sectors);
				setSpendByPlatform(platformResponse.data || []);
				setGlobalSectorTotal((prev) => {
					const fromApi = sectors[0]?.grandTotal;
					if (fromApi && fromApi > 0) return fromApi;
					const sum = sectors.reduce((acc, item) => acc + (item.totalSpent || 0), 0);
					return prev > 0 ? prev : sum;
				});

				const chartData = chartSeriesResponse.data || [];

				setChartSeries(chartData);
			} catch (error) {
				console.error('Error fetching filtered FinOps data:', error);
				setTrendData(null);
			} finally {
				setLoading(false);
				setTrendLoading(false);
			}
		};

		fetchFilteredData();
	}, [programName, effectiveSectorName, platformName, filterMonth, filterCustomStartDate, filterCustomEndDate]);

	const parseDateFilter = (filterMonth: string) => {
		const currentYear = new Date().getFullYear();

		if (filterMonth === 'custom' && filterCustomStartDate) {
			const year = filterCustomStartDate.getFullYear();
			const startMonth = filterCustomStartDate.getMonth() + 1;
			const endMonth = filterCustomEndDate ? filterCustomEndDate.getMonth() + 1 : startMonth;
			setCurrentFilterYear(year);
			return { year, startMonth, endMonth };
		}
		const currentMonth = new Date().getMonth() + 1;

		switch (filterMonth) {
			case 'today':
			case '0M':
				setCurrentFilterYear(currentYear);
				return { year: currentYear, startMonth: currentMonth, endMonth: currentMonth };
			case '24h':
			case '7d': {
				const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
				const yearForLastMonth = currentMonth === 1 ? currentYear - 1 : currentYear;
				setCurrentFilterYear(yearForLastMonth);
				return { year: yearForLastMonth, startMonth: lastMonth, endMonth: currentMonth };
			}
			case '30d':
			case '1M': {
				const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
				const yearForLastMonth = currentMonth === 1 ? currentYear - 1 : currentYear;
				setCurrentFilterYear(yearForLastMonth);
				return { year: yearForLastMonth, startMonth: lastMonth, endMonth: lastMonth };
			}
			case '0Y':
			case 'currentyear':
				setCurrentFilterYear(currentYear);
				return { year: currentYear, startMonth: 1, endMonth: currentMonth };
			case '1Y':
			case 'previousyear': {
				const previousYear = currentYear - 1;
				setCurrentFilterYear(previousYear);
				return { year: previousYear, startMonth: 1, endMonth: 12 };
			}
			case '3M':
				setCurrentFilterYear(currentYear);
				return { year: currentYear, startMonth: Math.max(1, currentMonth - 2), endMonth: currentMonth };
			case '6M':
				setCurrentFilterYear(currentYear);
				return { year: currentYear, startMonth: Math.max(1, currentMonth - 5), endMonth: currentMonth };
			case '9M':
				setCurrentFilterYear(currentYear);
				return { year: currentYear, startMonth: Math.max(1, currentMonth - 8), endMonth: currentMonth };
			default:
				setCurrentFilterYear(currentYear);
				return { year: currentYear, startMonth: undefined, endMonth: undefined };
		}
	};

	const ytd = summary?.totalTillDate || 0;
	const aop = summary?.totalAop || 0;
	const finalForecast = summary?.totalForecast || 0;

	const formattedYtd = formatNumberByUnits(ytd, true);
	const formattedAop = formatNumberByUnits(aop, true);
	const formattedFinalForecast = formatNumberByUnits(finalForecast, true);
	const forecastVsAopPct = calculateVariancePercentage(finalForecast, aop);
	const forecastVsAopPctNum = Number.parseFloat(forecastVsAopPct);
	const isForecastZero = forecastVsAopPctNum === 0;

	// Extract nested ternary operations for Final Forecast widget
	const forecastSubText = finalForecast == null ? '' : isForecastZero ? '0% vs AOP' : `${Math.abs(forecastVsAopPctNum)}% vs AOP`;

	const isOverBudget = forecastVsAopPctNum > 0;
	let forecastUnitIcon = null;
	let forecastColor = '#000000';

	if (!isForecastZero) {
		forecastUnitIcon = isOverBudget ? ArrowIconRed : ArrowIconGreen;
		forecastColor = isOverBudget ? 'error.main' : 'success.main';
	}

	const handleChartInsightsClick = (clickedMonth: string) => {
		handleOpenInsights('Spend vs Plan & Forecast Insights', 'FinOps Dashboard', clickedMonth);
	};

	const platformAopAverage =
		spendByPlatform.length > 0 ? spendByPlatform.reduce((sum, platform) => sum + (platform.aop || 0), 0) / 12 : 0;

	const calculatePercentage = (value: number, total: number) => {
		if (!total || total === 0) return 0;
		return (value / total) * 100;
	};

	const transformToHierarchicalData = (data: SpendData[], globalTotal?: number): HierarchicalData[] => {
		if (!data || data.length === 0) return [];
		const total = globalTotal ?? data.reduce((sum, item) => sum + (item.totalSpent || 0), 0);
		return data.map((item) => ({
			name: item.name,
			totalSpent: item.totalSpent,
			percentage: calculatePercentage(item.totalSpent, total)
		}));
	};

	const drawerCurrentFilters = useMemo(() => ({
		program: programName !== 'all' ? [{ label: programName, value: programName }] : [{ label: 'All Programs', value: 'all' }],
		sector: sectorName !== 'all' ? [{ label: sectorName, value: sectorName }] : [{ label: 'All Sectors', value: 'all' }],
		platform: platformName !== 'all' ? [{ label: platformName, value: platformName }] : [{ label: 'All Platforms', value: 'all' }],
		jobType: [],
		jobStatus: [],
		scheduleCategory: [],
		scheduledForNext: [],
		sourceCategory: [],
		sourceType: [],
		additionalFilters: []
	}), [programName, sectorName, platformName]);

	return (
		<PageWrapper sx={{ mt: 0, pt: '36px !important', px: '32px !important' }}>
			<FilterDrawer
				open={settingsDrawerOpen}
				onClose={() => { setSettingsDrawerOpen(false); setPendingDrawerField(null); }}
				onApply={(filters: FilterState) => {
					const prog = filters.program.find((p: GenericOption) => p.value !== 'all');
					const sect = filters.sector.find((s: GenericOption) => s.value !== 'all');
					const plat = filters.platform?.find((p: GenericOption) => p.value !== 'all');
					setProgramName(prog?.value ?? 'all');
					setSectorName(sect?.value ?? 'all');
					setPlatformName(plat?.value ?? 'all');
					setPendingDrawerField(null);
					setSettingsDrawerOpen(false);
				}}
				onFilterChange={(field: keyof FilterState, value: GenericOption[]) => {
					const isAll = value.length > 0 && value[0]?.value === 'all';
					if (value.length === 0) return;
					setPendingDrawerField(isAll ? null : String(field));
				}}
				onReset={() => {
					setPendingDrawerField(null);
					setProgramName('all');
					setSectorName('all');
					setPlatformName('all');
				}}
				currentFilters={drawerCurrentFilters}
				filterFields={[
					{
						label: 'Program', field: 'program', isSingleSelect: true,
						options: [{ label: 'All Programs', value: 'all' }, ...programsList.map(p => ({ label: p, value: p }))],
						value: programName !== 'all' ? [{ label: programName, value: programName }] : [{ label: 'All Programs', value: 'all' }],
						disabled: effectiveDrawerField !== null && effectiveDrawerField !== 'program',
					},
					{
						label: 'Sector', field: 'sector', isSingleSelect: true,
						options: [{ label: 'All Sectors', value: 'all' }, ...sectorsList.filter(s => s.isAllowed).map(s => ({ label: s.name, value: s.name }))],
						value: sectorName !== 'all' ? [{ label: sectorName, value: sectorName }] : [{ label: 'All Sectors', value: 'all' }],
						disabled: effectiveDrawerField !== null && effectiveDrawerField !== 'sector',
					},
					{
						label: 'Platform', field: 'platform', isSingleSelect: true,
						options: [{ label: 'All Platforms', value: 'all' }, ...platformsList.map(p => ({ label: p, value: p }))],
						value: platformName !== 'all' ? [{ label: platformName, value: platformName }] : [{ label: 'All Platforms', value: 'all' }],
						disabled: effectiveDrawerField !== null && effectiveDrawerField !== 'platform',
					},
				]}
				showTagDelete={false}
			/>

			<Box mt={2}>
				<Typography variant="subHeader" fontFamily="Montserrat" sx={{ mb: 1.5, display: 'block', color: 'textLight.main' }}>Financial Health Summary</Typography>
				<Grid container spacing={2.5}>
					<Grid item xs={12} md={6}>
						<Grid container spacing={2.5}>
							<Grid item xs={12} sm={6} id="total-spent-ytd">
								<NumberWidget
									title="Total Spend (YTD)"
									number={formattedYtd}
									isLoading={loading}
									hasTooltip={false}
									tooltipLabel=""
									units="MM"
									bgColor="#FBFDFF"
									color="#000000"
									disableHover
									valueFontSize="48px"
									valueFontWeight={400}
								/>
							</Grid>
							<Grid item xs={12} sm={6} id="aop">
								<NumberWidget
									title={`AOP ${currentFilterYear + 1}`}
									number={formattedAop}
									isLoading={loading}
									hasTooltip={false}
									tooltipLabel=""
									units="MM"
									bgColor="#FBFDFF"
									color="#000000"
									disableHover
									valueFontSize="48px"
									valueFontWeight={400}
								/>
							</Grid>
							<Grid item xs={12} sm={6} id="variance">
								<TrendIncrementWidget
									data={trendData}
									isLoading={trendLoading}
									year={currentFilterYear + 1}
									bgColor="#FBFDFF"
									color="#000000"
									valueFontSize="48px"
									valueFontWeight={400}
								/>
							</Grid>
							<Grid item xs={12} sm={6} id="forecast">
								<NumberWidget
									title={`Final Forecast ${currentFilterYear}`}
									number={formattedFinalForecast}
									isLoading={loading}
									hasTooltip={false}
									tooltipLabel=""
									units="MM"
									subText={forecastSubText}
									unitIcon={forecastUnitIcon}
									color={forecastColor}
									titleSx={{ color: '#000000' }}
									bgColor="#FBFDFF"
									disableHover
									valueFontSize="48px"
									valueFontWeight={400}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} md={6} id="monthly-trend">
						<FinOpsMonthlyTrendChart
							isLoading={loading}
							monthlyTotals={summary?.monthlyTotals || []}
							monthlyItemsData={getActiveChartData()}
							monthlyAopAverage={platformAopAverage}
							onInsightsClick={handleChartInsightsClick}
						/>
					</Grid>
				</Grid>

				<Box mt={3} id="spend-program">
					<SpendTable
						title="Spend By Program (Top 5)"
						data={transformToHierarchicalData(spendByProgram)}
						firstColumnLabel="Program"
					/>
				</Box>

				<Box mt={3} id="spend-sector">
					<SpendTable
						title="Spend By Sector (Top 5)"
						data={transformToHierarchicalData(
							shouldFilter && sectorNames.length > 0
								? spendBySector.filter((s) => sectorNames.includes(s.name))
								: spendBySector,
							globalSectorTotal
						)}
						firstColumnLabel="Sector"
					/>
				</Box>

				<Box mt={3} id="spend-platform">
					<SpendTable
						title="Spend By Platform (Top 5)"
						data={transformToHierarchicalData(spendByPlatform)}
						firstColumnLabel="Platform"
					/>
				</Box>
			</Box>
		</PageWrapper>
	);
};

export default FinOpsExecutiveDashboard;
